import { TestType } from "@app/provider/testData/types";
import { useContext } from "react";
import { ServiceContext } from "@app/provider";
import {
  FeedbackDataType,
  MessageReaction,
  QuickReplyType,
} from "@app/pippa/models";

type HistoricalMtmValueType = {
  __typename: string;
  date: string;
  mtm: number;
  resultCode: string;
};

type TestResultsType = {
  resultCode: string;
  historicalMtmValues: HistoricalMtmValueType[];
  currentMtmValue: number;
  mtmDelta?: number;
};

export interface CreateNewUserProps {
  patientUid: string;
  limsCaseId?: string;
  testType: TestType;
  firstName: string;
  testResults: TestResultsType;
}

export interface ChatStreamProps {
  chatId: string;
  message: string;
}

export interface ChatStreamChunk {
  delta?: {
    content?: string;
  };
  quickReplies?: QuickReplyType[];
  feedback?: FeedbackDataType;
  isInputDisabled?: boolean;
  messageId?: string;
  feedbackEligible?: boolean;
  done?: boolean;
}

export interface ChatActionProps {
  chatId: string;
  action: string;
}

export interface ChatActionMessage {
  role: string;
  content: string;
  quickReplies?: QuickReplyType[];
  createdAt?: string;
}

export interface ChatActionResponse {
  chatId: string;
  action: string;
  messages: ChatActionMessage[];
}

export interface ChatHistoryMessage {
  messageId: string;
  chatId?: string;
  role: string;
  content: string;
  createdAt: string;
  quickReplies?: QuickReplyType[];
  feedback?: { type: string };
  feedbackEligible?: boolean;
  reaction?: MessageReaction;
}

export interface ChatHistoryResponse {
  messages: ChatHistoryMessage[];
  isInputDisabled: boolean;
}

export interface SubmitFeedbackProps {
  chatId: string;
  type: string;
  score: number;
}

export interface SubmitMessageFeedbackProps {
  chatId: string;
  messageId: string;
  reaction: MessageReaction;
  text?: string;
}

type CreateNewUserData = {
  chatId: string;
};

type HeadersType = {
  "Content-Type": string;
  Authorization?: string;
};

enum ApiRequests {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
}

function handleQuickReplies(
  data: string,
  onChunk: (chunk: ChatStreamChunk) => void
): void {
  try {
    onChunk({ quickReplies: JSON.parse(data) });
  } catch (e) {
    console.error("Failed to parse quick replies:", e);
  }
}

function handleFeedback(
  data: string,
  onChunk: (chunk: ChatStreamChunk) => void
): void {
  try {
    onChunk({ feedback: JSON.parse(data) });
  } catch (e) {
    console.error("Failed to parse feedback:", e);
  }
}

function handleIsInputDisabled(
  data: string,
  onChunk: (chunk: ChatStreamChunk) => void
): void {
  try {
    const isInputDisabled = JSON.parse(data);
    onChunk({ isInputDisabled });
  } catch (e) {
    console.error("Failed to parse is_input_disabled:", e);
  }
}

function handleMessageId(
  data: string,
  onChunk: (chunk: ChatStreamChunk) => void
): void {
  try {
    const parsed = JSON.parse(data) as { messageId?: string };
    if (parsed?.messageId) {
      onChunk({ messageId: parsed.messageId });
    }
  } catch (e) {
    console.error("Failed to parse messageId:", e);
  }
}

function handleFeedbackEligible(
  data: string,
  onChunk: (chunk: ChatStreamChunk) => void
): void {
  try {
    const parsed = JSON.parse(data) as { feedbackEligible?: boolean };
    if (parsed?.feedbackEligible) {
      onChunk({ feedbackEligible: true });
    }
  } catch (e) {
    console.error("Failed to parse feedbackEligible:", e);
  }
}

function handleSSEDataLine(
  currentEvent: string,
  data: string,
  onChunk: (chunk: ChatStreamChunk) => void
): string {
  if (currentEvent === "message_id") {
    handleMessageId(data, onChunk);
    return "";
  }
  if (currentEvent === "feedback_eligible") {
    handleFeedbackEligible(data, onChunk);
    return "";
  }
  if (currentEvent === "quick_replies") {
    handleQuickReplies(data, onChunk);
    return "";
  }
  if (currentEvent === "feedback") {
    handleFeedback(data, onChunk);
    return "";
  }
  if (currentEvent === "is_input_disabled") {
    handleIsInputDisabled(data, onChunk);
    return "";
  }
  if (data) {
    onChunk({ delta: { content: data + " " } });
  }
  return currentEvent;
}

function processSSEEvent(
  eventText: string,
  onChunk: (chunk: ChatStreamChunk) => void,
  currentEvent: string
): string {
  const lines = eventText.split("\n");
  let nextEvent = currentEvent;

  for (const line of lines) {
    if (line.startsWith("event:")) {
      nextEvent = line.replace("event:", "").trim();
    } else if (line.startsWith("data:")) {
      const data = line.replace("data:", "").trim();
      nextEvent = handleSSEDataLine(nextEvent, data, onChunk);
    }
  }

  return nextEvent;
}

export const useApiController = () => {
  const { sessionService } = useContext(ServiceContext);

  const getHeaders = async () => {
    const headers: HeadersType = {
      "Content-Type": "application/json;charset=UTF-8",
    };
    const token = await sessionService.getToken();
    if (token?.accessToken) {
      headers.Authorization = `Bearer ${token.accessToken}`;
    }
    return headers;
  };

  const createNewUser = async (
    userData: CreateNewUserProps
  ): Promise<CreateNewUserData> => {
    const headers = await getHeaders();
    const response = await fetch("/pippa/api/users", {
      method: ApiRequests.POST,
      headers,
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      await responseErrorHandler(response);
    }

    return response.json();
  };

  const chatStream = async (
    streamData: ChatStreamProps,
    onChunk: (chunk: ChatStreamChunk) => void,
    onDone: () => void,
    signal?: AbortSignal
  ): Promise<void> => {
    const headers = await getHeaders();
    const response = await fetch("/pippa/ai/chat/stream", {
      method: ApiRequests.POST,
      headers,
      body: JSON.stringify(streamData),
      signal,
    });

    if (!response.ok) {
      await responseErrorHandler(response);
    }

    if (!response.body) {
      throw new Error("ReadableStream is not supported");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    let buffer = "";
    let currentEvent = "";

    try {
      while (true) {
        const { value, done } = await reader.read();

        if (done) {
          onDone();
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() ?? "";

        for (const event of events) {
          currentEvent = processSSEEvent(event, onChunk, currentEvent);
        }
      }
    } finally {
      reader.releaseLock();
    }
  };

  const getChatAction = async (
    actionData: ChatActionProps
  ): Promise<ChatActionResponse> => {
    const headers = await getHeaders();
    const response = await fetch("/pippa/ai/chat/action", {
      method: ApiRequests.POST,
      headers,
      body: JSON.stringify(actionData),
    });

    if (!response.ok) {
      await responseErrorHandler(response);
    }

    return response.json();
  };

  const getChatHistory = async (
    chatId: string
  ): Promise<ChatHistoryResponse> => {
    const headers = await getHeaders();
    const response = await fetch(`/pippa/chat/${chatId}/history`, {
      method: ApiRequests.GET,
      headers,
    });

    if (!response.ok) {
      await responseErrorHandler(response);
    }

    const data = await response.json();

    const messages = Array.isArray(data?.messages)
      ? (data.messages as ChatHistoryMessage[])
      : [];
    const isInputDisabled = Boolean(data?.isInputDisabled);

    return { messages, isInputDisabled };
  };

  const submitFeedback = async (
    feedbackData: SubmitFeedbackProps
  ): Promise<void> => {
    const headers = await getHeaders();
    const response = await fetch("/pippa/api/feedback", {
      method: ApiRequests.POST,
      headers,
      body: JSON.stringify(feedbackData),
    });

    if (!response.ok) {
      await responseErrorHandler(response);
    }
  };

  const submitMessageFeedback = async (
    feedbackData: SubmitMessageFeedbackProps
  ): Promise<void> => {
    const headers = await getHeaders();
    const response = await fetch("/pippa/api/message-feedback", {
      method: ApiRequests.POST,
      headers,
      body: JSON.stringify(feedbackData),
    });

    if (!response.ok) {
      await responseErrorHandler(response);
    }
  };

  const responseErrorHandler = async (response: Response): Promise<Error> => {
    const data = await response.json();
    throw new Error(data.error.message);
  };

  return {
    createNewUser,
    chatStream,
    getChatAction,
    getChatHistory,
    submitFeedback,
    submitMessageFeedback,
  };
};
